// Curated brand similarity map (Fashion AI v1, tops only).
//
// IMPORTANT: This file MUST stay byte-identical to
// `supabase/functions/_shared/brandSimilarity.ts`. Edge functions cannot
// import from `src/lib`, so the module is duplicated. If you change one,
// change both.

export type FitPole =
  | "small_slim"
  | "true_to_size"
  | "true_to_relaxed"
  | "true_to_generous"
  | "large_boxy";

export type SimilarityConfidence = "low" | "medium" | "high";

export interface BrandSimilarityEntry {
  similar: string[];
  fit: FitPole;
  confidence: SimilarityConfidence;
  note?: string;
}

export interface CategorySimilarityMap {
  women: Record<string, BrandSimilarityEntry>;
  men: Record<string, BrandSimilarityEntry>;
}

export const BRAND_SIMILARITY_TOPS: CategorySimilarityMap = {
  women: {
    "Zara": {
      similar: ["Mango", "Stradivarius", "Bershka", "Pull & Bear"],
      fit: "small_slim",
      confidence: "medium",
    },
    "ASOS": {
      similar: ["Topshop", "New Look", "Miss Selfridge", "River Island"],
      fit: "true_to_size",
      confidence: "medium",
    },
    "H&M": {
      similar: ["Mango", "Zara", "Bershka", "Vero Moda"],
      fit: "small_slim",
      confidence: "medium",
    },
    "COS": {
      similar: ["Arket", "& Other Stories", "Uniqlo", "Weekday"],
      fit: "large_boxy",
      confidence: "medium",
    },
    "& Other Stories": {
      similar: ["COS", "Arket", "Mango", "Sezane"],
      fit: "true_to_relaxed",
      confidence: "medium",
    },
    "Next": {
      similar: ["M&S", "John Lewis", "White Stuff", "FatFace"],
      fit: "true_to_generous",
      confidence: "medium",
    },
  },
  men: {
    "Zara": {
      similar: ["Mango", "Pull & Bear", "Bershka", "Massimo Dutti"],
      fit: "small_slim",
      confidence: "medium",
    },
    "ASOS": {
      similar: ["Topman", "River Island", "New Look", "Bershka"],
      fit: "true_to_size",
      confidence: "medium",
    },
    "H&M": {
      similar: ["Uniqlo", "Mango", "Zara", "Jack & Jones"],
      fit: "small_slim",
      confidence: "medium",
    },
    "COS": {
      similar: ["Arket", "Uniqlo", "Weekday", "Massimo Dutti"],
      fit: "large_boxy",
      confidence: "medium",
    },
    "& Other Stories": {
      similar: ["Arket", "COS", "Uniqlo", "Sandro"],
      fit: "true_to_relaxed",
      confidence: "low",
      note: "& Other Stories has no native menswear; substituted closest equivalents",
    },
    "Next": {
      similar: ["M&S", "John Lewis", "FatFace", "White Stuff"],
      fit: "true_to_generous",
      confidence: "medium",
    },
  },
};

// Brands explicitly excluded from similarity inference due to inconsistent sizing
export const UNRELIABLE_FIT_BRANDS = ["PrettyLittleThing", "PLT", "Missguided", "Boohoo"];

export interface AnchorMatch {
  anchor: string;
  fit: FitPole;
  confidence: SimilarityConfidence;
}

/**
 * Given a target brand (the retailer the user is shopping at NOW),
 * return the set of anchor brands whose verdict could plausibly transfer.
 */
export function findAnchorsForBrand(
  target: string,
  gender: "women" | "men" | "non-binary",
  category: "tops" | "bottoms" = "tops",
): AnchorMatch[] {
  if (category !== "tops") return [];
  if (!target) return [];

  const lookupGender: "women" | "men" = gender === "non-binary" ? "women" : gender;
  const map = BRAND_SIMILARITY_TOPS[lookupGender];

  // Direct anchor match: target IS an anchor
  if (map[target]) {
    return [{ anchor: target, fit: map[target].fit, confidence: "high" }];
  }

  // Reverse lookup
  const matches: AnchorMatch[] = [];
  for (const [anchor, entry] of Object.entries(map)) {
    if (entry.similar.includes(target)) {
      matches.push({ anchor, fit: entry.fit, confidence: entry.confidence });
    }
  }
  return matches;
}

/**
 * Check if a brand is in the unreliable list — should be excluded from
 * similarity inference entirely.
 */
export function isUnreliableBrand(brand: string): boolean {
  return UNRELIABLE_FIT_BRANDS.includes(brand);
}
